
import React from 'react';
import { Assessment } from '@/types/assessment';
import { Response } from '@/hooks/assessment/types/responseTypes';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getMaturityLevelLabel, getMaturityLevelColor } from '@/utils/maturityLevelUtils';
import NoResponsesPlaceholder from './NoResponsesPlaceholder';

interface ResponsesListProps {
  assessment: Assessment;
  responses: Response[];
}

const ResponsesList = ({ assessment, responses }: ResponsesListProps) => {
  // If no responses, show placeholder
  if (responses.length === 0) {
    return <NoResponsesPlaceholder assessment={assessment} />;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold">Individual Responses</h3>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Current Maturity</TableHead>
              <TableHead>Target Maturity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {responses.map((response) => {
              const { personal_info, submitted_at, maturity_scores, scores_by_category } = response;
              
              // Calculate average maturity score across all categories
              let currentScoreSum = 0;
              let targetScoreSum = 0;
              let categoryCount = 0;
              
              // Try to use maturity_scores first
              if (maturity_scores && Object.keys(maturity_scores).length > 0) {
                Object.values(maturity_scores).forEach(score => {
                  currentScoreSum += score.score || 0;
                  targetScoreSum += score.target || 0;
                  categoryCount++;
                });
              } 
              // Fall back to scores_by_category if needed
              else if (scores_by_category && Object.keys(scores_by_category).length > 0) {
                Object.values(scores_by_category).forEach(score => {
                  currentScoreSum += score.current || 0;
                  targetScoreSum += score.target || 0;
                  categoryCount++;
                });
              }
              
              const currentScore = categoryCount > 0 ? currentScoreSum / categoryCount : 0;
              const targetScore = categoryCount > 0 ? targetScoreSum / categoryCount : 0;
              
              return (
                <TableRow key={response.id}>
                  <TableCell className="font-medium">{personal_info?.name || 'Unknown'}</TableCell>
                  <TableCell>{personal_info?.company || 'N/A'}</TableCell>
                  <TableCell>{personal_info?.email || 'N/A'}</TableCell>
                  <TableCell>{new Date(submitted_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="whitespace-nowrap">
                        {Math.floor(currentScore)} / 4
                      </Badge>
                      <Progress value={(currentScore / 4) * 100} className="h-2 w-16" />
                    </div>
                  </TableCell>
                  <TableCell>
                    {targetScore > 0 ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="whitespace-nowrap">
                          {Math.floor(targetScore)} / 4
                        </Badge>
                        <Progress value={(targetScore / 4) * 100} className="h-2 w-16" />
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not set</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ResponsesList;
